import { useState, useRef, useCallback, useMemo } from "react";
import { useEditor, EditorContent, type JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import UnderlineExt from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import { ProtocolEditorToolbar } from "./protocol-editor/ProtocolEditorToolbar";
import { Pencil, Eye, Save, Lock, Unlock } from "lucide-react";
import { useStudyBuilderStore } from "@/stores/study-builder-store";
import { useUpdateBuildArtifact } from "@/hooks/use-protocol-builder";
import type { StudyDocumentSection } from "@/api/protocol-types";

// ── Conversion: Sections → TipTap JSONContent ──────────

function sectionsToTipTapContent(sections: StudyDocumentSection[]): JSONContent {
  const content: JSONContent[] = [];

  for (const section of sections) {
    // Section heading (H2)
    content.push({
      type: "heading",
      attrs: { level: 2 },
      content: [{ type: "text", text: section.title }],
    });

    // Section content — split by double newline into paragraphs
    if (section.content) {
      const paragraphs = section.content.split(/\n\n+/);
      for (const para of paragraphs) {
        if (!para.trim()) continue;

        // Check if it's a sub-heading (starts with ### or ##)
        if (para.startsWith("### ")) {
          content.push({
            type: "heading",
            attrs: { level: 3 },
            content: [{ type: "text", text: para.replace(/^###\s*/, "") }],
          });
        } else if (para.startsWith("- ") || para.startsWith("* ")) {
          // Bullet list
          const items = para.split(/\n/).filter((l) => l.trim());
          content.push({
            type: "bulletList",
            content: items.map((item) => ({
              type: "listItem",
              content: [
                {
                  type: "paragraph",
                  content: [{ type: "text", text: item.replace(/^[-*]\s*/, "") }],
                },
              ],
            })),
          });
        } else {
          content.push({
            type: "paragraph",
            content: [{ type: "text", text: para }],
          });
        }
      }
    }

    // Table data
    if (section.tableData && section.tableData.length > 0) {
      const cols = Object.keys(section.tableData[0]);
      content.push({
        type: "table",
        content: [
          // Header row
          {
            type: "tableRow",
            content: cols.map((col) => ({
              type: "tableHeader",
              content: [
                {
                  type: "paragraph",
                  content: [{ type: "text", text: col.replace(/_/g, " ") }],
                },
              ],
            })),
          },
          // Data rows
          ...section.tableData.map((row) => ({
            type: "tableRow" as const,
            content: cols.map((col) => ({
              type: "tableCell" as const,
              content: [
                {
                  type: "paragraph" as const,
                  content: [{ type: "text" as const, text: String(row[col] ?? "") }],
                },
              ],
            })),
          })),
        ],
      });
    }

    // Horizontal rule between sections
    content.push({ type: "horizontalRule" });
  }

  // Remove trailing HR
  if (content.length > 0 && content[content.length - 1].type === "horizontalRule") {
    content.pop();
  }

  return { type: "doc", content };
}

// ── Conversion: TipTap JSONContent → Sections ──────────

function tipTapContentToSections(doc: JSONContent): StudyDocumentSection[] {
  const sections: StudyDocumentSection[] = [];
  const nodes = doc.content ?? [];
  let currentSection: { title: string; contentParts: string[]; tableData?: Record<string, string>[] } | null = null;

  for (const node of nodes) {
    if (node.type === "heading" && node.attrs?.level === 2) {
      // Start new section
      if (currentSection) {
        sections.push({
          id: `section-${sections.length}`,
          title: currentSection.title,
          content: currentSection.contentParts.join("\n\n"),
          order: sections.length,
          tableData: currentSection.tableData,
        });
      }
      const title = extractText(node);
      currentSection = { title, contentParts: [] };
    } else if (node.type === "horizontalRule") {
      // Skip — just a visual separator
    } else if (node.type === "table" && currentSection) {
      // Extract table data
      currentSection.tableData = extractTableData(node);
    } else if (currentSection) {
      const text = extractText(node);
      if (text.trim()) {
        if (node.type === "heading" && node.attrs?.level === 3) {
          currentSection.contentParts.push(`### ${text}`);
        } else if (node.type === "bulletList") {
          const items = (node.content ?? []).map((li) => `- ${extractText(li)}`);
          currentSection.contentParts.push(items.join("\n"));
        } else {
          currentSection.contentParts.push(text);
        }
      }
    }
  }

  // Push last section
  if (currentSection) {
    sections.push({
      id: `section-${sections.length}`,
      title: currentSection.title,
      content: currentSection.contentParts.join("\n\n"),
      order: sections.length,
      tableData: currentSection.tableData,
    });
  }

  return sections;
}

function extractText(node: JSONContent): string {
  if (node.text) return node.text;
  if (node.content) return node.content.map(extractText).join("");
  return "";
}

function extractTableData(table: JSONContent): Record<string, string>[] {
  const rows = table.content ?? [];
  if (rows.length < 2) return [];

  // First row = headers
  const headers = (rows[0].content ?? []).map((cell) => extractText(cell).trim());

  // Remaining rows = data
  return rows.slice(1).map((row) => {
    const cells = row.content ?? [];
    const record: Record<string, string> = {};
    headers.forEach((h, i) => {
      record[h] = cells[i] ? extractText(cells[i]).trim() : "";
    });
    return record;
  });
}

// ── Document Editor Component ──────────────────────────

interface DocumentEditorProps {
  type: string;
  id: string;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  protocol: "Protocol Document",
  protocol_synopsis: "Protocol Synopsis",
  resource_plan: "Resource Plan",
  rbm_plan: "Risk-Based Monitoring Plan",
  safety_management_plan: "Safety Management Plan",
  training_plan: "Training Plan",
  raci_matrix: "RACI Matrix",
};

export function DocumentEditor({ type, id }: DocumentEditorProps) {
  const studyDocuments = useStudyBuilderStore((s) => s.studyDocuments);
  const build = useStudyBuilderStore((s) => s.build);
  const doc = studyDocuments.find((d) => d.id === id) ?? studyDocuments.find((d) => d.documentType === type);
  const updateArtifact = useUpdateBuildArtifact();

  const [mode, setMode] = useState<"view" | "edit">("view");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const isLocked = doc?.locked ?? false;
  const buildId = build?.id ?? "";

  const initialContent = useMemo(() => {
    if (!doc) return { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }] };
    return sectionsToTipTapContent(doc.sections);
  }, [doc]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      UnderlineExt,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Highlight,
      Placeholder.configure({ placeholder: "Start writing your document..." }),
    ],
    content: initialContent,
    editable: mode === "edit" && !isLocked,
    onUpdate: ({ editor: ed }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        handleAutoSave(ed.getJSON());
      }, 2000);
    },
  });

  // Sync editable state
  if (editor) {
    const shouldBeEditable = mode === "edit" && !isLocked;
    if (editor.isEditable !== shouldBeEditable) {
      editor.setEditable(shouldBeEditable);
    }
  }

  const handleAutoSave = useCallback(
    (content: JSONContent) => {
      if (!doc || !buildId) return;
      const sections = tipTapContentToSections(content);
      updateArtifact.mutate({
        buildId,
        artifactType: doc.documentType,
        artifactId: doc.id,
        updates: { sections },
      });
    },
    [doc, buildId, updateArtifact],
  );

  const handleManualSave = () => {
    if (!editor || !doc) return;
    handleAutoSave(editor.getJSON());
  };

  if (!doc) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No {DOC_TYPE_LABELS[type] ?? "document"} generated yet.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2.5">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-foreground">{doc.title}</h3>
          <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600">
            {doc.status}
          </span>
          <span className="text-[10px] text-muted-foreground">v{doc.version}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Lock toggle */}
          <button
            onClick={() => {
              if (!buildId || !doc) return;
              updateArtifact.mutate({
                buildId,
                artifactType: doc.documentType,
                artifactId: doc.id,
                updates: { locked: !isLocked },
              });
            }}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
            title={isLocked ? "Unlock document" : "Lock document"}
          >
            {isLocked ? <Lock className="h-3.5 w-3.5 text-amber-500" /> : <Unlock className="h-3.5 w-3.5" />}
          </button>

          {/* View/Edit toggle */}
          <div className="flex rounded-md border border-border">
            <button
              onClick={() => setMode("view")}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === "view" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <Eye className="mr-1 inline h-3 w-3" />
              View
            </button>
            <button
              onClick={() => setMode("edit")}
              disabled={isLocked}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === "edit" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              } disabled:opacity-40`}
            >
              <Pencil className="mr-1 inline h-3 w-3" />
              Edit
            </button>
          </div>

          {mode === "edit" && (
            <button
              onClick={handleManualSave}
              disabled={updateArtifact.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-3 w-3" />
              Save
            </button>
          )}
        </div>
      </div>

      {/* Toolbar (edit mode only) */}
      {mode === "edit" && !isLocked && (
        <div className="border-b border-border bg-card/50">
          <ProtocolEditorToolbar editor={editor} />
        </div>
      )}

      {/* Editor content */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-8 py-6">
          {mode === "edit" ? (
            <EditorContent
              editor={editor}
              className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-th:text-foreground prose-td:text-muted-foreground [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[400px]"
            />
          ) : (
            <div className="space-y-6">
              {doc.sections.map((section) => (
                <div key={section.id} className="space-y-2">
                  <h2 className="text-base font-semibold text-foreground border-b border-border pb-1">
                    {section.title}
                  </h2>

                  {section.content && (
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {section.content}
                    </div>
                  )}

                  {section.tableData && section.tableData.length > 0 && (
                    <div className="overflow-x-auto rounded-md border border-border">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/50">
                            {Object.keys(section.tableData[0]).map((col) => (
                              <th key={col} className="px-3 py-2 text-left font-medium text-foreground capitalize">
                                {col.replace(/_/g, " ")}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.tableData.map((row, i) => (
                            <tr key={i} className="border-t border-border">
                              {Object.values(row).map((val, j) => (
                                <td key={j} className="px-3 py-2 text-muted-foreground">
                                  {val}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
